import { validateFlowManifest } from './manifest-validator'
import WorkflowManifestCompilationError from './workflow-manifest-compilation-error'

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key)

const cloneJson = value => JSON.parse(JSON.stringify(value))

const FLOW_SETTINGS_DEFAULTS = Object.freeze({
    renderer: 'vertical-collapse',
    nextStepOpenDelayMs: 0,
    scrollToNextStep: false,
    nextStepScrollDelayMs: 0,
    progressPersistenceMode: 'blocking',
    initialStepMode: 'first',
})

const RENDERER_SETTINGS_DEFAULTS = Object.freeze({
    contentContainer: 'card',
    showStepTitle: true,
    showStepDescription: true,
    showPreviousButton: true,
    contentBordered: false,
    stepperSize: 'sm',
    saveStatus: 'none',
    showSaveStatusText: false,
})

const NAVIGATION_SETTINGS_DEFAULTS = Object.freeze({
    scrollOnOpen: 'none',
    blockForwardOnInvalidSteps: false,
})

const deepFreeze = value => {
    if (value && typeof value === 'object' && !Object.isFrozen(value)) {
        Object.freeze(value)
        Object.keys(value).forEach(key => deepFreeze(value[key]))
    }

    return value
}

const applySettingsDefaults = (schema, value) => {
    if (!schema || !schema.type) {
        return cloneJson(value)
    }

    if (schema.type === 'object') {
        const source = value || {}
        const result = {}

        Object.keys(schema.properties || {}).forEach(key => {
            const propertySchema = schema.properties[key]

            if (hasOwn(source, key)) {
                result[key] = applySettingsDefaults(propertySchema, source[key])
            } else if (hasOwn(propertySchema, 'default')) {
                result[key] = cloneJson(propertySchema.default)
            }
        })

        return result
    }

    return cloneJson(value)
}

const compileCondition = (id, conditionRegistry) => {
    if (id === null) {
        return null
    }

    return Object.freeze({
        id,
        evaluate: context => conditionRegistry.evaluate(id, context),
    })
}

const compileStep = (step, stepRegistry, conditionRegistry) => {
    const implementation = stepRegistry.resolve(step.type, step.typeVersion)
    const { descriptor } = implementation
    const title = step.titleOverride === null
        ? Object.freeze({
            key: descriptor.defaultTitleKey,
            fallback: descriptor.fallbackTitle,
        })
        : Object.freeze({
            key: null,
            fallback: step.titleOverride,
        })
    const navigationTitleOverride = typeof step.navigationTitleOverride === 'undefined'
        ? null
        : step.navigationTitleOverride
    const navigationTitle = navigationTitleOverride === null
        ? Object.freeze({
            key: descriptor.defaultNavigationTitleKey || title.key,
            fallback: descriptor.fallbackNavigationTitle || title.fallback,
        })
        : Object.freeze({
            key: null,
            fallback: navigationTitleOverride,
        })
    const descriptionOverride = typeof step.descriptionOverride === 'undefined'
        ? null
        : step.descriptionOverride
    const description = descriptionOverride === null
        ? Object.freeze({
            key: descriptor.defaultDescriptionKey || null,
            fallback: descriptor.fallbackDescription || null,
        })
        : Object.freeze({
            key: null,
            fallback: descriptionOverride,
        })

    return Object.freeze({
        id: step.id,
        type: step.type,
        typeVersion: step.typeVersion,
        order: step.order,
        enabled: step.enabled,
        titleOverride: step.titleOverride,
        title,
        navigationTitleOverride,
        navigationTitle,
        descriptionOverride,
        description,
        numbered: step.numbered,
        review: Object.freeze({
            enabled: false,
            initiallyExpanded: false,
            ...(step.review || {}),
        }),
        settings: deepFreeze(applySettingsDefaults(descriptor.settingsSchema, step.settings)),
        availability: Object.freeze({
            visibleWhen: compileCondition(step.availability.visibleWhen, conditionRegistry),
            disabledWhen: compileCondition(step.availability.disabledWhen, conditionRegistry),
        }),
        validation: Object.freeze({ ...step.validation }),
        completion: Object.freeze({
            persistProgress: true,
            invalidBehavior: 'stay',
            scrollAfterExecution: false,
            ...step.completion,
        }),
        mount: Object.freeze({ ...step.mount }),
        navigation: Object.freeze({ ...step.navigation }),
        legacy: step.legacy ? Object.freeze({ ...step.legacy }) : null,
        descriptor,
        component: implementation.component,
        createController: implementation.createController,
        createReviewContribution: implementation.createReviewContribution || null,
        migrateSettings: implementation.migrateSettings || null,
    })
}

const compileFlowManifest = (manifest, dependencies) => {
    const validationResult = validateFlowManifest(manifest, dependencies)

    if (!validationResult.isValid) {
        throw new WorkflowManifestCompilationError(validationResult.errors)
    }

    const { stepRegistry, conditionRegistry } = dependencies
    const rawManifest = deepFreeze(cloneJson(manifest))
    const steps = rawManifest.steps
        .slice()
        .sort((left, right) => left.order - right.order)
        .map(step => compileStep(step, stepRegistry, conditionRegistry))
    const stepsById = new Map(steps.map(step => [step.id, step]))
    const stepsByLegacyPersistedStep = new Map(
        steps
            .filter(step => step.legacy)
            .map(step => [step.legacy.persistedStep, step]),
    )
    const rendererSettings = Object.freeze({
        ...RENDERER_SETTINGS_DEFAULTS,
        ...(rawManifest.settings.rendererSettings || {}),
    })
    const navigationSettings = Object.freeze({
        ...NAVIGATION_SETTINGS_DEFAULTS,
        ...(rawManifest.settings.navigation || {}),
    })

    return Object.freeze({
        id: rawManifest.id,
        version: rawManifest.version,
        schemaVersion: rawManifest.schemaVersion,
        title: rawManifest.title || null,
        settings: Object.freeze({
            ...FLOW_SETTINGS_DEFAULTS,
            ...rawManifest.settings,
            rendererSettings,
            navigation: navigationSettings,
        }),
        manifest: rawManifest,
        steps: Object.freeze(steps),
        getStep(id) {
            return stepsById.get(id) || null
        },
        getStepByLegacyPersistedStep(persistedStep) {
            return stepsByLegacyPersistedStep.get(persistedStep) || null
        },
    })
}

export { compileFlowManifest }
export default compileFlowManifest
